import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Image,
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
} from 'react-native';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Path } from 'react-native-svg';
import { getTukarPoinDetail, redeemReward } from '../services/rewardsApi';
import { getUserData } from '../services/userDataService';
import AppHeader from '../components/AppHeader';
import {
  TUKAR_POIN_COLORS,
  formatPoinNumber,
  formatRp,
  formatDateRange,
  categoryBadge,
  isVoucherReward,
  needsShipping,
  isInStock,
  isPeriodActive,
  plainRewardText,
  isPromoConfigured,
  isInPromo,
  successCopy,
  isLinkAjaReward,
  claimInfoCopy,
  claimLimitState,
  claimWarnCopy,
  findRewardSku,
  firstInStockSku,
  needsSkuPick,
  skuActivePoin,
  skuImageUrl,
  skuInStock,
  skuVarianGroups,
  rewardSkus,
  stockLabel,
} from '../utils/tukarPoin';
import { formatPhoneLocal } from '../utils/phoneInput';

const FALLBACK_IMAGE = require('../../assets/rewards/reward-image-1.png');

function PointIcon({ size = 14, muted = false }) {
  const star = Math.max(5, Math.round(size * 0.75));
  const bg = muted ? '#CBD5E1' : '#FBBF24';
  return (
    <View
      style={[
        styles.pointIcon,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: bg,
        },
      ]}
    >
      <Image 
        source={require('../../assets/icons/z-point-solid.svg')} 
        style={{ 
          width: star, 
          height: star, 
          tintColor: '#FFFFFF',
        }}
        resizeMode="contain"
      />
    </View>
  );
}

function PeriodLine({ text, promo, strike }) {
  if (!text) return null;
  return (
    <Text
      style={[
        styles.dateText,
        promo && styles.datePromo,
        strike && styles.dateStrike,
        strike && styles.dateRowStrike,
      ]}
    >
      Periode Tukar {text}
    </Text>
  );
}

function CloudCheckIcon({ size = 46, color = '#00C16A' }) {
  const petalCount = 8;
  const petal = size * 0.4;
  const radius = size * 0.32;
  const checkBox = Math.round(size * 0.5);
  return (
    <View style={{ width: size, height: size }}>
      {Array.from({ length: petalCount }, (_, i) => {
        const angle = (i / petalCount) * Math.PI * 2;
        return (
          <View
            key={i}
            style={{
              position: 'absolute',
              width: petal,
              height: petal,
              borderRadius: petal / 2,
              backgroundColor: color,
              left: size / 2 + Math.cos(angle) * radius - petal / 2,
              top: size / 2 + Math.sin(angle) * radius - petal / 2,
            }}
          />
        );
      })}
      <View
        style={{
          position: 'absolute',
          left: size * 0.12,
          top: size * 0.12,
          width: size * 0.76,
          height: size * 0.76,
          borderRadius: size,
          backgroundColor: color,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Svg width={checkBox} height={checkBox} viewBox="0 0 24 24">
          <Path
            d="M5 13 L10 18 L19.5 7"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth={5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      </View>
    </View>
  );
}

function SkuChip({ label, selected, disabled, onPress }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={[
        styles.skuChip,
        selected && styles.skuChipOn,
        disabled && styles.skuChipDisabled,
      ]}
    >
      <Text style={[styles.skuChipText, selected && styles.skuChipTextOn, disabled && styles.skuChipTextDisabled]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

function RewardSkuPicker({ item, selectedSkuId, onSelect }) {
  const skus = rewardSkus(item);
  if (skus.length < 2) return null;
  const groups = skuVarianGroups(item);
  const showVarian = groups.length > 1;
  const selected = findRewardSku(item, selectedSkuId);
  const varianId = String(selected?.id_varian || groups[0]?.id || 0);
  const sizeSkus = skus.filter((sku) => String(sku.id_varian || 0) === varianId);

  return (
    <View style={styles.skuPicker}>
      {showVarian ? (
        <View style={styles.skuGroup}>
          <Text style={styles.skuLabel}>Pilih varian:</Text>
          <View style={styles.skuRow}>
            {groups.map((group) => (
              <SkuChip
                key={group.id}
                label={group.nama}
                selected={String(group.id) === varianId}
                onPress={() => {
                  const next = skus.find((sku) => String(sku.id_varian || 0) === String(group.id) && skuInStock(sku))
                    || skus.find((sku) => String(sku.id_varian || 0) === String(group.id));
                  if (next) onSelect(Number(next.id));
                }}
              />
            ))}
          </View>
        </View>
      ) : null}
      <View style={styles.skuGroup}>
        <Text style={styles.skuLabel}>Pilih size/warna:</Text>
        <View style={styles.skuRow}>
          {sizeSkus.map((sku) => (
            <SkuChip
              key={sku.id}
              label={sku.ukuran || sku.sku || 'SKU'}
              selected={Number(sku.id) === Number(selectedSkuId)}
              disabled={!skuInStock(sku)}
              onPress={() => onSelect(Number(sku.id))}
            />
          ))}
        </View>
      </View>
    </View>
  );
}

function SummaryRow({ label, value, note, total, free, plain, negative, sisa }) {
  return (
    <View style={[styles.summaryBlock, sisa && styles.summarySisa]}>
      <View style={styles.rowBetween}>
        <Text style={[styles.summaryLabel, total && styles.summaryTotalLabel]}>{label}</Text>
        {free ? (
          <Text style={styles.freeOngkir}>{value}</Text>
        ) : plain ? (
          <Text style={styles.summaryPlain}>{value}</Text>
        ) : (
          <View style={styles.summaryPriceRow}>
            {negative ? <Text style={styles.summaryMinus}>-</Text> : null}
            <PointIcon size={12} />
            <Text style={styles.summaryValue}>{value}</Text>
          </View>
        )}
      </View>
      {note ? <Text style={styles.summaryNote}>{note}</Text> : null}
    </View>
  );
}

export default function TukarPoinDetailScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const rewardId = Number(route.params?.rewardId || 0);
  const [item, setItem] = useState(route.params?.preview || null);
  const [saldoPoin, setSaldoPoin] = useState(Number(route.params?.saldoPoin || 0));
  const [loading, setLoading] = useState(!item);
  const [claiming, setClaiming] = useState(false);
  const [success, setSuccess] = useState(null);
  const [infoPopup, setInfoPopup] = useState(null);
  const [linkajaConfirm, setLinkajaConfirm] = useState(null);
  const [selectedSkuId, setSelectedSkuId] = useState(0);

  const voucher = isVoucherReward(item);
  const physical = !!(item && needsShipping(item) && !voucher);
  const { limitReached } = claimLimitState(item);
  const showInfo = (title, body) => setInfoPopup({ title, body });
  const selectedSku = findRewardSku(item, selectedSkuId);
  const itemPoin = skuActivePoin(item, selectedSku);
  const strikePoin = selectedSku ? Number(selectedSku.harga_poin || 0) : Number(item?.harga_poin || 0);
  const enoughPoints = itemPoin <= Number(saldoPoin || 0);

  const skuIdKey = rewardSkus(item).map((sku) => sku.id).join(',');
  useEffect(() => {
    const first = firstInStockSku(item);
    if (!first) {
      setSelectedSkuId(0);
      return;
    }
    setSelectedSkuId((current) => {
      const cur = findRewardSku(item, current);
      if (cur && skuInStock(cur)) return Number(current);
      return Number(first.id);
    });
  }, [item?.id, skuIdKey]);

  const loadDetail = useCallback(async () => {
    if (!rewardId) return;
    try {
      const response = await getTukarPoinDetail(rewardId);
      const data = response?.data || {};
      setItem(data);
      if (data.saldo_poin != null) setSaldoPoin(Number(data.saldo_poin));
    } catch (e) {
      Alert.alert('Gagal memuat', e.message || 'Coba lagi.');
    } finally {
      setLoading(false);
    }
  }, [rewardId]);

  useFocusEffect(
    useCallback(() => {
      loadDetail();
    }, [loadDetail]),
  );

  const badge = item ? categoryBadge(item) : null;
  const promoConfigured = isInPromo(item);
  const skuUrl = skuImageUrl(item, selectedSku);
  const imageSource = skuUrl ? { uri: skuUrl } : FALLBACK_IMAGE;
  const period = item ? formatDateRange(item.tanggal_mulai, item.tanggal_berakhir) : '';
  const promoPeriod = promoConfigured
    ? formatDateRange(item?.promo_tanggal_mulai, item?.promo_tanggal_berakhir)
    : '';
  const sisaPoin = Math.max(0, Number(saldoPoin || 0) - itemPoin);
  const kurangPoin = Math.max(0, itemPoin - Number(saldoPoin || 0));
  const inStock = isInStock(item, selectedSku);
  const periodActive = isPeriodActive(item);
  const claimWarn = claimWarnCopy(item, {
    enoughPoints,
    kurangPoin,
    inStock,
    periodActive,
  });
  const showShippingNote = physical && periodActive && enoughPoints && !limitReached && inStock;
  const deskripsiText = plainRewardText(item?.deskripsi) || plainRewardText(item?.product_description);
  const detailText = plainRewardText(item?.detail_reward);
  const syaratKetentuanText = plainRewardText(item?.syarat_ketentuan);
  const showStrike = promoConfigured && strikePoin > itemPoin;
  const stockText = stockLabel(item, selectedSku);

  const specRows = physical ? [
    ['Harga Retail', (selectedSku?.harga_retail || item?.harga_retail) ? formatRp(selectedSku?.harga_retail || item?.harga_retail) : '-'],
    ['Kategori', item?.product_kategori || item?.kategori || '-'],
    ['Varian', selectedSku?.varian || item?.product_varian || '-'],
    ['Ukuran', selectedSku?.ukuran || item?.product_ukuran || '-'],
  ] : [];

  const mainDisabled = !item || claiming || !!claimWarn
    || (needsSkuPick(item) && selectedSkuId < 1);

  const submitClaim = async () => {
    setClaiming(true);
    try {
      const response = await redeemReward(rewardId, { id_produk: selectedSkuId || 0 });
      const data = response?.data || {};
      const usedPoin = itemPoin;
      setSuccess({
        message: response?.message || '',
        data,
        voucher,
        nama: item?.nama_reward || '',
        gambar: skuUrl || item?.gambar_url || '',
        kategori: categoryBadge(item)?.label || (voucher ? 'Voucher' : 'Hadiah Program'),
        poinDigunakan: usedPoin,
        sisaPoin: data.saldo_poin != null ? Number(data.saldo_poin) : Math.max(0, Number(saldoPoin || 0) - usedPoin),
      });
      if (data.saldo_poin != null) setSaldoPoin(Number(data.saldo_poin));
    } catch (e) {
      setInfoPopup(claimInfoCopy(e.message));
    } finally {
      setClaiming(false);
    }
  };

  const onPressClaim = () => {
    if (claiming) return;
    if (claimWarn) {
      showInfo(claimWarn.title, claimWarn.body);
      return;
    }
    if (needsSkuPick(item) && selectedSkuId < 1) {
      showInfo('Pilih Varian', 'Pilih varian/SKU terlebih dahulu.');
      return;
    }
    if (physical) {
      navigation.navigate('TukarPoinShipping', {
        rewardId,
        preview: item,
        saldoPoin,
        selectedSkuId,
      });
      return;
    }
    // if (isLinkAjaReward(item)) {
    //   confirmLinkAjaClaim();
    //   return;
    // }
    submitClaim();
  };

  const confirmLinkAjaClaim = async () => {
    try {
      const user = await getUserData();
      const phone = formatPhoneLocal(user?.user_telp || user?.telp || user?.phone || '');
      setLinkajaConfirm({ missing: !phone, phone });
    } catch (e) {
      setLinkajaConfirm({ missing: true, phone: '' });
    }
  };

  const closeSuccess = () => {
    setSuccess(null);
    navigation.goBack();
  };

  const openHistory = () => {
    setSuccess(null);
    navigation.navigate('TukarPoinHistory', { caller: 'tukar poin' });
  };

  const header = (
    <AppHeader title="Detail Reward" onBack={() => navigation.goBack()} />
  );

  if (loading && !item) {
    return (
      <SafeAreaView style={styles.safe}>
        {header}
        <View style={styles.center}>
          <ActivityIndicator color="#F59E0B" />
        </View>
      </SafeAreaView>
    );
  }

  if (!item) {
    return (
      <SafeAreaView style={styles.safe}>
        {header}
        <View style={styles.center}>
          <Text style={styles.empty}>Reward tidak ditemukan</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      {header}
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.hero}>
          <Image source={imageSource} style={styles.heroImage} resizeMode="contain" />
        </View>

        <View style={styles.section}>
          <View style={styles.badgeRow}>
            {badge?.label ? (
              <View style={[styles.catBadge, { backgroundColor: badge.bg }]}>
                <Text style={[styles.catBadgeText, { color: badge.color }]}>{badge.label}</Text>
              </View>
            ) : null}
            {promoConfigured ? (
              <LinearGradient
                colors={['#FFE4E6', '#F43F5E']}
                start={{ x: 0, y: 0.5 }}
                end={{ x: 1, y: 0.5 }}
                style={styles.promoBadge}
              >
                <Text style={styles.promoBadgeText}>Hot Deals</Text>
              </LinearGradient>
            ) : null}
          </View>

          <Text style={styles.title}>{item.nama_reward}</Text>

          <View>
            <View style={styles.priceRow}>
              <PointIcon size={18} />
              <Text style={styles.price}>{formatPoinNumber(itemPoin)}</Text>
            </View>
            {showStrike ? (
              <View style={styles.strikeRow}>
                <PointIcon size={18} />
                <Text style={styles.priceStrike}>{formatPoinNumber(strikePoin)}</Text>
                <View style={styles.priceStrikeLine} pointerEvents="none" />
              </View>
            ) : null}
            {stockText ? <Text style={styles.stockText}>{stockText}</Text> : null}
          </View>

          <View style={[styles.periodWrap, { display: 'none' }]}>
            {promoPeriod ? <PeriodLine text={promoPeriod} promo /> : null}
            {period && promoConfigured ? (
              <PeriodLine text={period} strike />
            ) : period && !promoConfigured ? (
              <PeriodLine text={period} />
            ) : null}
          </View>
        </View>

        {physical ? (
          <RewardSkuPicker item={item} selectedSkuId={selectedSkuId} onSelect={setSelectedSkuId} />
        ) : null}

        {showShippingNote ? (
          <View style={styles.infoBanner}>
            <Ionicons name="information-circle-outline" size={18} color="#3B82F6" />
            <Text style={styles.infoBannerText}>
              Produk yang berhasil diklaim akan dikirim ke alamat tersimpan Anda.
            </Text>
          </View>
        ) : null}

        {physical || deskripsiText ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Deskripsi Reward</Text>
            <Text style={styles.body}>{deskripsiText || '-'}</Text>
          </View>
        ) : null}

        {physical || detailText ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Detail Reward</Text>
            <Text style={styles.body}>{detailText || '-'}</Text>
          </View>
        ) : null}

        {voucher || syaratKetentuanText ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Syarat & Ketentuan</Text>
            <Text style={styles.body}>{syaratKetentuanText || '-'}</Text>
          </View>
        ) : null}

        {specRows.length ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Spesifikasi Produk Reward</Text>
            <View style={styles.specList}>
              {specRows.map(([label, value]) => (
                <View key={label} style={styles.specRow}>
                  <Text style={styles.specLabel}>{label}</Text>
                  <Text style={styles.specValue}>{value}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}

        {specRows.length ? <View style={styles.claimDivider} /> : null}

        <View style={styles.claimSection}>
          <Text style={styles.claimTitle}>Klaim Reward</Text>
          <View style={styles.claimList}>
            <SummaryRow label="Poin Anda" value={formatPoinNumber(saldoPoin)} />

            {claimWarn ? (
              <View style={styles.warnBox}>
                <Text style={styles.warnTitle}>{claimWarn.title}</Text>
                <Text style={styles.warnText}>{claimWarn.body}</Text>
              </View>
            ) : (
              <>
                <SummaryRow label="Poin Digunakan" value={formatPoinNumber(itemPoin)} negative />
                <SummaryRow label="Sisa Poin" value={formatPoinNumber(sisaPoin)} sisa />
                {physical ? (
                  <SummaryRow
                    label="Ongkos Kirim"
                    value="Saat klaim"
                    plain
                    note="*ongkos kirim reward menggunakan poin yang Anda miliki"
                  />
                ) : null}
              </>
            )}
          </View>
          <TouchableOpacity
            style={[styles.claimBtn, mainDisabled && styles.claimBtnDisabled]}
            onPress={onPressClaim}
            disabled={claiming}
          >
            {claiming ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.claimBtnText}>Klaim Reward</Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      <Modal visible={!!infoPopup} transparent animationType="fade" onRequestClose={() => setInfoPopup(null)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.infoCard}>
            <Text style={styles.infoTitle}>{infoPopup?.title}</Text>
            <Text style={styles.infoBody}>{infoPopup?.body}</Text>
            <TouchableOpacity style={styles.infoBtn} onPress={() => setInfoPopup(null)}>
              <Text style={styles.infoBtnText}>Mengerti</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!linkajaConfirm}
        transparent
        animationType="fade"
        onRequestClose={() => setLinkajaConfirm(null)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.infoCard}>
            <TouchableOpacity
              style={styles.linkajaClose}
              onPress={() => setLinkajaConfirm(null)}
              hitSlop={12}
            >
              <Ionicons name="close" size={20} color="#94A3B8" />
            </TouchableOpacity>
            <View style={styles.linkajaIconWrap}>
              <Ionicons
                name={linkajaConfirm?.missing ? 'alert-circle' : 'help'}
                size={28}
                color="#FFFFFF"
              />
            </View>
            <Text style={styles.infoTitle}>
              {linkajaConfirm?.missing ? 'Pemberitahuan' : 'Konfirmasi'}
            </Text>
            {linkajaConfirm?.missing ? (
              <Text style={styles.infoBody}>
                Belum ada nomor telepon yang terdaftar di akunmu. Masukkan nomor telepon yang terdaftar di aplikasi LinkAja lewat menu Profil.
              </Text>
            ) : (
              <Text style={styles.infoBody}>
                Reward ini adalah saldo LinkAja. Untuk memastikan Kamu mendapatkan reward, pastikan nomor teleponmu yang terdaftar di Zoolyfe (
                <Text style={styles.infoBodyBold}>{linkajaConfirm?.phone}</Text>
                ), telah terdaftar di aplikasi LinkAja. Apakah Kamu yakin akan melanjutkan?
              </Text>
            )}
            {linkajaConfirm?.missing ? (
              <View style={styles.linkajaActions}>
                <TouchableOpacity
                  style={[styles.infoBtn, styles.linkajaPrimaryBtn]}
                  onPress={() => {
                    setLinkajaConfirm(null);
                    navigation.navigate('EditProfile');
                  }}
                >
                  <Text style={styles.infoBtnText}>Lengkapi profil</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.linkajaDenyBtn}
                  onPress={() => setLinkajaConfirm(null)}
                >
                  <Text style={styles.linkajaDenyText}>Nanti saja</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.linkajaActions}>
                <TouchableOpacity
                  style={[styles.infoBtn, styles.linkajaPrimaryBtn]}
                  onPress={() => {
                    setLinkajaConfirm(null);
                    submitClaim();
                  }}
                >
                  <Text style={styles.infoBtnText}>Ya, lanjutkan</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.linkajaDenyBtn}
                  onPress={() => setLinkajaConfirm(null)}
                >
                  <Text style={styles.linkajaDenyText}>Nanti saja</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      </Modal>

      <Modal visible={!!success} transparent animationType="fade">
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <TouchableOpacity style={styles.modalClose} onPress={closeSuccess} hitSlop={12}>
                <Ionicons name="close" size={24} color="#94A3B8" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalContent}>
              <View style={styles.successIconWrap}>
                <CloudCheckIcon size={46} color="#00C16A" />
              </View>
              <Text style={styles.modalTitle}>
                {successCopy(success?.data, voucher).title}
              </Text>
              <Text style={styles.modalBody}>
                {success?.data?.status_redeem === 'menunggu_bayar_ongkir'
                  ? successCopy(success?.data, voucher).body
                  : (
                    <>
                      {success?.kategori ? `${success.kategori} ` : ''}
                      <Text style={styles.modalBodyBold}>{success?.nama || item?.nama_reward}</Text>
                      {' berhasil ditukarkan dan telah ditambahkan ke riwayat penukaranmu'}
                    </>
                  )}
              </Text>
              <Image
                source={success?.gambar ? { uri: success.gambar } : imageSource}
                style={styles.modalImage}
                resizeMode="contain"
              />
              <View style={styles.modalSummary}>
                <View style={styles.modalSummaryRow}>
                  <Text style={styles.modalSummaryLabel}>Poin digunakan</Text>
                  <Text style={styles.modalSummaryValue}>
                    {formatPoinNumber(success?.poinDigunakan)} poin
                  </Text>
                </View>
                <View style={styles.modalDash} />
                <View style={styles.modalSummaryRow}>
                  <Text style={styles.modalSummaryLabel}>Sisa Poin Kamu</Text>
                  <Text style={styles.modalSummaryRemain}>
                    {formatPoinNumber(success?.sisaPoin ?? saldoPoin)} poin
                  </Text>
                </View>
              </View>
              <View style={styles.modalActions}>
                <TouchableOpacity style={styles.modalOutline} onPress={closeSuccess}>
                  <Text style={styles.modalOutlineText}>Kembali ke Katalog</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.modalFilled} onPress={openHistory}>
                  <Text style={styles.claimBtnText}>Lihat Riwayat Penukaran</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#FFFFFF' },
  content: { paddingBottom: Platform.OS === 'ios' ? 28 : 16 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  empty: { fontFamily: 'Poppins', color: TUKAR_POIN_COLORS.slate },
  hero: {
    height: 220,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroImage: { width: '100%', height: '100%' },
  section: { paddingHorizontal: 20, paddingTop: 20 },
  badgeRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  catBadge: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4 },
  catBadgeText: { fontFamily: 'Poppins-Medium', fontSize: 11, fontWeight: '500' },
  promoBadge: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4 },
  promoBadgeText: { color: '#fff', fontFamily: 'Poppins-Medium', fontSize: 11, fontWeight: '500' },
  title: {
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    fontWeight: '500',
    color: '#000',
    lineHeight: 20,
    marginBottom: 10,
  },
  pointIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  priceRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  price: { fontFamily: 'Poppins-SemiBold', fontSize: 16, fontWeight: '600', color: '#F59E0B' },
  strikeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginTop: 4,
    position: 'relative',
  },
  priceStrike: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    color: '#F59E0B',
  },
  priceStrikeLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '50%',
    marginTop: -0.75,
    height: 1.5,
    backgroundColor: '#F43F5E',
  },
  stockText: {
    fontFamily: 'Poppins-Medium',
    fontSize: 12,
    fontWeight: '500',
    color: '#64748B',
    marginTop: 6,
  },
  periodWrap: { marginTop: 10 },
  dateText: {
    fontFamily: 'Poppins-Medium',
    fontSize: 12,
    fontWeight: '500',
    color: '#94A3B8',
  },
  datePromo: { color: '#F43F5E' },
  dateStrike: { fontSize: 10, color: '#CBD5E1', textDecorationLine: 'line-through' },
  dateRowStrike: { marginTop: 4 },
  skuPicker: { paddingHorizontal: 20, paddingTop: 20, gap: 12 },
  skuGroup: { gap: 8 },
  skuLabel: { fontFamily: 'Poppins-Medium', fontSize: 13, fontWeight: '500', color: '#000' },
  skuRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  skuChip: {
    borderWidth: 1,
    borderColor: '#1E293B',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#FFFFFF',
  },
  skuChipOn: { backgroundColor: '#1E293B' },
  skuChipDisabled: { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC', opacity: 0.55 },
  skuChipText: { fontFamily: 'Poppins-Medium', fontSize: 12, fontWeight: '500', color: '#1E293B' },
  skuChipTextOn: { color: '#FFFFFF' },
  skuChipTextDisabled: { color: '#94A3B8' },
  infoBanner: {
    marginHorizontal: 20,
    marginTop: 20,
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: '#BFDBFE',
    borderRadius: 8,
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoBannerText: {
    flex: 1,
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#000000',
    lineHeight: 18,
  },
  sectionTitle: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    color: '#000',
    marginBottom: 8,
  },
  body: { fontFamily: 'Poppins', fontSize: 12, color: '#1E293B', lineHeight: 18 },
  specList: { gap: 8 },
  specRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  specLabel: { fontFamily: 'Poppins-Medium', fontSize: 12, fontWeight: '500', color: '#64748B' },
  specValue: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 12,
    fontWeight: '600',
    color: '#1E293B',
    flexShrink: 1,
    textAlign: 'right',
  },
  claimDivider: {
    height: 2,
    backgroundColor: '#F1F5F9',
    marginTop: 20,
  },
  claimSection: { paddingHorizontal: 20, paddingTop: 20, paddingBottom: 8 },
  claimTitle: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginBottom: 12,
  },
  claimList: {},
  summaryBlock: { paddingVertical: 4 },
  summarySisa: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderTopColor: '#F1F5F9',
    borderBottomColor: '#F1F5F9',
    paddingTop: 12,
    paddingBottom: 12,
    marginTop: 4,
    marginBottom: 4,
  },
  summaryLabel: { fontFamily: 'Poppins-Medium', fontSize: 12, fontWeight: '500', color: '#64748B' },
  summaryTotalLabel: { color: '#1E293B' },
  summaryPriceRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  summaryValue: { fontFamily: 'Poppins-SemiBold', fontSize: 12, fontWeight: '600', color: '#F59E0B' },
  summaryMinus: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 12,
    fontWeight: '600',
    color: '#F59E0B',
    marginRight: 0,
  },
  summaryPlain: { fontFamily: 'Poppins-Medium', fontSize: 12, color: '#94A3B8' },
  summaryNote: {
    fontFamily: 'Poppins',
    fontSize: 10,
    fontStyle: 'italic',
    color: '#94A3B8',
    marginTop: 2,
  },
  freeOngkir: { fontFamily: 'Poppins-SemiBold', fontSize: 12, color: '#16A34A' },
  rowBetween: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  warnBox: {
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#FECACA',
    borderRadius: 8,
    padding: 8,
    gap: 4,
    marginTop: 8,
  },
  warnTitle: { fontFamily: 'Poppins-SemiBold', fontSize: 12, fontWeight: '600', color: '#EF4444' },
  warnText: { fontFamily: 'Poppins', fontSize: 12, color: '#000' },
  quoteError: { fontFamily: 'Poppins', fontSize: 12, color: '#E11D48' },
  metaMuted: { fontFamily: 'Poppins', fontSize: 12, color: TUKAR_POIN_COLORS.slate },
  link: { fontFamily: 'Poppins-SemiBold', fontSize: 13, color: '#2563EB' },
  addAddr: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 10,
    padding: 12,
  },
  addAddrText: { fontFamily: 'Poppins-Medium', color: TUKAR_POIN_COLORS.navy },
  addrCard: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
    gap: 2,
  },
  addrCardActive: { borderColor: TUKAR_POIN_COLORS.gold, backgroundColor: '#FFFBEB' },
  addrTitle: { fontFamily: 'Poppins-SemiBold', fontSize: 13, color: TUKAR_POIN_COLORS.navy, flex: 1 },
  addrSub: { fontFamily: 'Poppins', fontSize: 12, color: TUKAR_POIN_COLORS.slate },
  addrLine: { fontFamily: 'Poppins', fontSize: 12, color: '#334155' },
  rateCard: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 12,
    marginBottom: 8,
  },
  metodeRow: { flexDirection: 'row', gap: 8 },
  metodeBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  metodeBtnActive: { borderColor: TUKAR_POIN_COLORS.navy, backgroundColor: TUKAR_POIN_COLORS.surface },
  metodeText: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 11,
    lineHeight: 16,
    color: TUKAR_POIN_COLORS.slate,
  },
  metodeTextActive: { color: TUKAR_POIN_COLORS.navy },
  metodeSub: { fontFamily: 'Poppins', fontSize: 12, color: TUKAR_POIN_COLORS.slate, marginTop: 4 },
  poinOngkirBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    padding: 12,
    backgroundColor: '#F8FAFC',
  },
  poinOngkirHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  poinOngkirLabel: { fontFamily: 'Poppins-SemiBold', fontSize: 12, color: TUKAR_POIN_COLORS.navy },
  poinOngkirRate: { fontFamily: 'Poppins', fontSize: 11, color: '#F59E0B' },
  poinInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  poinInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    color: '#0F172A',
    textAlign: 'right',
    width: '100%',
  },
  poinInputSuffix: { fontFamily: 'Poppins-Medium', fontSize: 12, color: TUKAR_POIN_COLORS.slate },
  poinMaxBtn: {
    backgroundColor: '#F59E0B',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    justifyContent: 'center',
  },
  poinMaxBtnText: { color: '#fff', fontFamily: 'Poppins-SemiBold', fontSize: 13, fontWeight: '600' },
  poinInputHint: {
    fontFamily: 'Poppins',
    fontSize: 11,
    color: TUKAR_POIN_COLORS.slate,
    marginTop: 8,
    lineHeight: 16,
  },
  insRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 8,
  },
  insCopy: { flex: 1, paddingRight: 12 },
  insTitle: { fontFamily: 'Poppins-SemiBold', fontSize: 13, color: TUKAR_POIN_COLORS.navy },
  insHint: { fontFamily: 'Poppins', fontSize: 11, color: TUKAR_POIN_COLORS.slate, marginTop: 2 },
  qrPreview: {
    width: 180,
    height: 180,
    alignSelf: 'center',
    marginTop: 12,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
  },
  claimBtn: {
    backgroundColor: '#1E293B',
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: 'center',
    marginTop: 24,
  },
  claimBtnDisabled: { opacity: 0.5 },
  claimBtnText: { color: '#fff', fontFamily: 'Poppins-Medium', fontSize: 14, fontWeight: '500' },
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,0.45)',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  sheet: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: Platform.OS === 'ios' ? 28 : 16,
    maxHeight: '88%',
  },
  sheetHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#E2E8F0',
    alignSelf: 'center',
    marginBottom: 12,
  },
  sheetScroll: { marginTop: 12, marginBottom: 8, maxHeight: 360 },
  sheetLabel: { fontFamily: 'Poppins-SemiBold', fontSize: 13, color: '#000', marginBottom: 8 },
  pickerList: {
    maxHeight: 168,
    marginTop: 8,
  },
  ongkirHint: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: TUKAR_POIN_COLORS.slate,
    marginTop: 12,
    marginBottom: 16,
    lineHeight: 18,
  },
  sheetClaimBtn: { marginTop: 8 },
  infoCard: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    position: 'relative',
  },
  infoTitle: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    textAlign: 'center',
  },
  infoBody: {
    fontFamily: 'Poppins',
    fontSize: 13,
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 20,
  },
  infoBtn: {
    backgroundColor: '#1E293B',
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 20,
  },
  infoBtnText: {
    color: '#fff',
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    fontWeight: '500',
  },
  infoBodyBold: {
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '700',
    color: '#1E293B',
  },
  linkajaClose: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 2,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  linkajaIconWrap: {
    alignSelf: 'center',
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#E46B29',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  linkajaActions: {
    marginTop: 16,
    gap: 10,
  },
  linkajaPrimaryBtn: {
    marginTop: 0,
  },
  linkajaDenyBtn: {
    borderWidth: 1,
    borderColor: '#1E293B',
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  linkajaDenyText: {
    color: '#1E293B',
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    fontWeight: '500',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
    backgroundColor: '#fff',
    borderRadius: 20,
    overflow: 'hidden',
  },
  modalHeader: {
    paddingTop: 16,
    paddingHorizontal: 16,
    paddingBottom: 32,
    alignItems: 'flex-end',
  },
  modalClose: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    alignItems: 'stretch',
  },
  successIconWrap: {
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: 'Poppins-Bold',
    fontSize: 14,
    fontWeight: '700',
    color: '#00C16A',
    textAlign: 'center',
  },
  modalBody: {
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: '400',
    color: '#64748B',
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 18,
  },
  modalBodyBold: {
    fontFamily: 'Poppins-Bold',
    fontWeight: '700',
    color: '#334155',
  },
  modalImage: {
    width: '100%',
    height: 170,
    marginTop: 16,
  },
  modalSummary: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginTop: 16,
    marginBottom: 16,
  },
  modalActions: {
    gap: 12,
  },
  modalSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  modalSummaryLabel: {
    fontFamily: 'Poppins',
    fontSize: 14,
    fontWeight: '400',
    color: '#64748B',
  },
  modalSummaryValue: {
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  modalSummaryRemain: {
    fontFamily: 'Poppins-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    color: '#334155',
  },
  modalDash: {
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#CBD5E1',
    marginVertical: 10,
  },
  modalOutline: {
    borderWidth: 1,
    borderColor: '#64748B',
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  modalOutlineText: {
    color: '#64748B',
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    fontWeight: '500',
  },
  modalFilled: {
    backgroundColor: '#1E293B',
    borderRadius: 8,
    paddingVertical: 8,
    alignItems: 'center',
  },
});
